import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmail, verifyEmailOtp } from '../services/apiService';

const InterviewSignInModal = ({ isOpen, onClose, emailId, mobileNumber }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState(emailId || '');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Timer effect for resend OTP
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(timer => {
          if (timer <= 1) {
            setCanResend(true);
            return 0;
          }
          return timer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  // Start timer when OTP step is reached
  const startResendTimer = () => {
    setResendTimer(10);
    setCanResend(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // Call sign-in API with email
      const response = await signInWithEmail(email);
      if (response.success) {
        setStep('otp');
        startResendTimer(); // Start 10-second timer
      } else {
        setError(response.error || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp || otp.length !== 4) {
      setError('Please enter a valid 4-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await verifyEmailOtp(email, otp);
      if (response.success) {
        // Store user data
        localStorage.setItem('currentUser', JSON.stringify({
          email: email,
          mobileNumber: mobileNumber,
          isAuthenticated: true
        }));

        // Navigate to interviews screen
        sessionStorage.removeItem('pendingInterviewAccess');
        // Mark interview flow as authenticated to prevent re-auth within this flow
        try {
          sessionStorage.setItem('interviewAuthenticated', 'true');
        } catch (_) {}
        // Notify listeners within the app that interview auth succeeded
        try {
          window.dispatchEvent(new CustomEvent('interview-auth-success'));
        } catch (_) {}
        
        // Navigate to interviews list (Pending/Completed tabs) and preserve current query string in the URL
        navigate({ pathname: '/interviews', search: window.location.search }, {
          state: {
            emailId: email, // use the entered email, not the prop
            mobileNumber: mobileNumber
          },
          replace: false
        });
      } else {
        setError(response.error || 'Invalid OTP');
      }
    } catch (err) {
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await signInWithEmail(email);
      if (response.success) {
        startResendTimer(); // Restart 10-second timer
      } else {
        setError(response.error || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: window.innerWidth <= 768 ? '10px' : '20px'
      }}onClick={(e) => e.stopPropagation()} // Prevent closing on backdrop click
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: window.innerWidth <= 768 ? '12px' : '12px',
        padding: '0',
        width: '100%',
        maxWidth: window.innerWidth <= 768 ? '95vw' : '340px',
        position: 'relative',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
          padding: window.innerWidth <= 768 ? '16px' : '24px',
          color: 'white',
          position: 'relative',
          borderRadius: '12px 12px 0 0'
        }}>
          <h2 style={{ 
            margin: '0 0 8px 0', 
            fontSize: window.innerWidth <= 768 ? '20px' : '24px', 
            fontWeight: '700',
            lineHeight: '1.2'
          }}>
            <i className="fas fa-calendar-check" style={{ marginRight: '12px' }}></i>
            Interview Access
          </h2>
          <p style={{ 
            margin: '0', 
            fontSize: window.innerWidth <= 768 ? '14px' : '16px', 
            opacity: '0.9',
            lineHeight: '1.4'
          }}>
            Sign in to access your interview feedback
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: window.innerWidth <= 768 ? '24px 16px' : '32px 24px' }}>
          {step === 'email' ? (
            <form onSubmit={handleEmailSubmit}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
                <p style={{ 
                  margin: '8px 0 0 0', 
                  fontSize: '12px', 
                  color: '#6b7280' 
                }}>
                  We'll send a one-time password (OTP) to this email to verify your access.
                </p>
              </div>

              {error && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <p style={{ margin: '0', fontSize: '14px', color: '#dc2626' }}>
                    {error}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                  color: 'white',
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane" style={{ marginRight: '8px' }}></i>
                    Send OTP
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Enter OTP
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="Enter 4-digit OTP"
                  maxLength="4"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '18px',
                    textAlign: 'center',
                    letterSpacing: '2px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
                <p style={{ 
                  margin: '8px 0 0 0', 
                  fontSize: '12px', 
                  color: '#6b7280' 
                }}>
                  OTP sent to {email}
                </p>
              </div>

              {error && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <p style={{ margin: '0', fontSize: '14px', color: '#dc2626' }}>
                    {error}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                  color: 'white',
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: '12px'
                }}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
                    Verify & Continue
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleResendOtp}
                disabled={loading || !canResend}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: canResend ? '#2563eb' : '#9ca3af',
                  padding: '8px',
                  border: `1px solid ${canResend ? '#2563eb' : '#9ca3af'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (loading || !canResend) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <i className="fas fa-redo" style={{ marginRight: '8px' }}></i>
                {resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : 'Resend OTP'}
              </button>

              <button
                type="button"
                onClick={() => setStep('email')}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#6b7280',
                  padding: '8px',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                <i className="fas fa-arrow-left" style={{ marginRight: '8px' }}></i>
                Back to Email
              </button>
              
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #f59e0b'
              }}>
                <p style={{ 
                  margin: '0', 
                  fontSize: '12px', 
                  color: '#92400e',
                  textAlign: 'center'
                }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                  You must complete sign-in to access the interview system
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewSignInModal;
